import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Plus, RefreshCw, Pencil, Eye, Upload, Trash2, Loader2 } from 'lucide-react';
import { DashboardShell } from '../layout/DashboardShell';
import { UserMenu } from '../components/UserMenu';
import { DashPanel } from '../components/DashPanel';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/contexts/AuthContext';
import type { BlogPost } from '../types/blog';
import { listBlogPosts, publishBlogPost, deleteBlogPost } from './blogApi';

export function BlogHome() {
  const { getIdToken } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Busca só ao entrar na página + botão manual — sem listener/polling (spec do blog, seção 6).
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Sem token de autenticação');
      const { posts: fetched } = await listBlogPosts(token);
      setPosts(fetched);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar artigos');
    } finally {
      setLoading(false);
    }
  }, [getIdToken]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePublish = async (post: BlogPost) => {
    setBusyId(post.id);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Sem token de autenticação');
      const { post: updated } = await publishBlogPost(post.id, {}, token);
      setPosts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Falha ao publicar artigo');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (post: BlogPost) => {
    if (!window.confirm(`Excluir "${post.titulo}"? Essa ação não pode ser desfeita.`)) return;
    setBusyId(post.id);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Sem token de autenticação');
      await deleteBlogPost(post.id, token);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Falha ao excluir artigo');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <DashboardShell mode="blog">
      <header className="shrink-0 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-foreground">Blog</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Notícias, relatos e novidades do Projeto Conectar
          </p>
        </div>
        <UserMenu />
      </header>

      <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-blue-100 dark:bg-background border-t border-l border-border rounded-tl-2xl">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            Atualizar
          </Button>
          <Button onClick={() => navigate('/dashboard/blog/new')}>
            <Plus className="h-4 w-4" />
            Novo artigo
          </Button>
        </div>

        {error && (
          <DashPanel padding="sm" className="border-destructive/40 bg-destructive/5 text-sm text-destructive">
            {error}
          </DashPanel>
        )}

        <DashPanel padding="none">
          {loading ? (
            <div className="p-10 flex items-center justify-center text-muted-foreground text-sm gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Carregando artigos…
            </div>
          ) : posts.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              Nenhum artigo ainda. Clique em "Novo artigo" para começar.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Título</TableHead>
                  <TableHead>Autor</TableHead>
                  <TableHead>Atualizado</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.map((post) => (
                  <TableRow key={post.id}>
                    <TableCell className="font-medium text-foreground max-w-[320px] truncate">
                      {post.titulo}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{post.autorNome}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDistanceToNow(new Date(post.atualizadoEm), { addSuffix: true, locale: ptBR })}
                    </TableCell>
                    <TableCell>
                      <span
                        className={
                          post.status === 'publicado'
                            ? 'inline-flex items-center rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400 px-2.5 py-0.5 text-xs font-medium'
                            : 'inline-flex items-center rounded-full bg-muted text-muted-foreground px-2.5 py-0.5 text-xs font-medium'
                        }
                      >
                        {post.status === 'publicado' ? 'Publicado' : 'Rascunho'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Editar"
                          disabled={busyId === post.id}
                          onClick={() => navigate(`/dashboard/blog/${post.id}/edit`)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {post.status === 'publicado' && (
                          <Button variant="ghost" size="icon" title="Ver no site" asChild>
                            <a href={`/blog/${post.slug}`} target="_blank" rel="noreferrer">
                              <Eye className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                        {post.status === 'rascunho' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Publicar"
                            disabled={busyId === post.id}
                            onClick={() => handlePublish(post)}
                          >
                            <Upload className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Excluir"
                          disabled={busyId === post.id}
                          onClick={() => handleDelete(post)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DashPanel>
      </div>
    </DashboardShell>
  );
}
